import React from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import moment from 'moment'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { mod, getDayArray } from '../../util'
import '../../assets/css/components/assignment-popup-calendar.css'
library.add(faChevronLeft, faChevronRight)

/*
 * A popout calendar for date selection that returns clicked-on dates in ISO8601 format.
 *
 * Useful features to add as this component grows:
 * - Starting the calendar at a particular month/year
 * - Moving the caret to differnt positions and sides of the calendar box
 * - Different stylings
 * - Different placeholder text
 */
class AssignmentPopupCalendar extends React.Component {
  static propTypes = {
    /** Optional date if one is currently selected. Must be ISO8601 format. */
    selectedDate: PropTypes.string,
    /** Callback to execute when a date is clicked on. Must expect ISO8601 format. */
    onSelect: PropTypes.func.isRequired
  }

  constructor(props) {
    super(props)
    const now = moment()
    this.state = {
      day:    parseInt(now.format('D')),
      month:  parseInt(now.format('M')),
      year:   parseInt(now.format('YYYY')),
      cachedDate: ''
    }
  }

  // Used to generate a moment that represents the current month / year that the user is currently looking at.
  getMonthMoment = ()=> moment([this.state.month, 1, this.state.year].join('/'))

  shortDayNames = ['Sun', 'Mon','Tue','Wed','Thu','Fri','Sat']

  changeMonth = (increment=true) =>{
    // The month is stored in the state 1-indexed, ie 1 = January, not 0 = January since it's easier to
    //    convert the month to a valid moment().
    // The modulus range for months is zero-indexed however (ie, [0, 11] is the range when you mod([1,12], 12)).
    //
    // We can convert from non-zero-indexed to zero-index just for the purposes of this formula.
    // If incrementing: monthVal = (month + 1) - 1 = month. The addition is for the incrementation, the minus is
    //    to convert to zero indexing.
    // If decrementing: monthVal = (month - 1) - 1 = month - 2. The first subtraction is for decrementation, and
    //    the second minus is to convert to zero indexing.
    // We then take the monthVal modulus 12 and convert it back to non-zero-indexed, hence the final plus one.
    const newMonth = mod((increment ? this.state.month : this.state.month-2), 12) + 1

    let newYear = this.state.year
    if(newMonth > this.state.month && !increment) {
      newYear--
    } else if(newMonth < this.state.month && increment) {
      newYear++
    }

    this.setState({ month: newMonth, year: newYear })
  }

  getGridSquareClasses = obj => {
    let classNames = ['grid-square no-select']

    if(obj.isWeekday && obj.isCurMonth){ classNames.push('primary') }
    if(obj.isSelected){ classNames.push('selected') }

    return classNames.join(' ')
  }

  //Checks if the newly selected date is on a different month than what's currently on the calendar. If it is, update the current month and year. 
  //This check should happen when the component updates.
  updateSelectedMonth = ()=> {
    const selectedMonth = _.isEmpty(this.props.selectedDate) ? '' : moment(this.props.selectedDate).format('M')
    const selectedYear = _.isEmpty(this.props.selectedDate) ? '' : moment(this.props.selectedDate).format('YYYY')

    //If the selectedDate exists, is different than the cached date, and different than the current month or year that the calendar is showing, update the calendar
    if(!_.isEmpty(this.props.selectedDate) && (!_.isEqual(this.state.cachedDate, this.props.selectedDate)) &&  (!_.isEqual(this.state.month, selectedMonth) || !_.isEqual(this.state.year, selectedYear)))  {
      this.setState({ month: selectedMonth, year: selectedYear, cachedDate: this.props.selectedDate })
    }
  }

  componentDidUpdate(){
    this.updateSelectedMonth()
  }

  getGridSquareBackgroundClasses = obj => {
    let classNames = ['grid-square-container']
    if (obj.isToday) { classNames.push('today') }
    return classNames.join(' ')
  }

  render() {
    return(
      <div className='assignment-popup-calendar'> <div className="arrow-up"></div>
        <div className='calendar-header'>
          <span onClick={()=>this.changeMonth(false)} className='nav left'>
            <FontAwesomeIcon icon='chevron-left'/>
          </span>
          <span unselectable='on' onMouseDown={()=>false} className='month-text'>
            {this.getMonthMoment().format('MMMM')}&nbsp;{this.getMonthMoment().format('YYYY')}
          </span>
          <span onClick={()=>this.changeMonth(true)} className='nav right'>
            <FontAwesomeIcon icon='chevron-right'/>
          </span>
        </div>
        <div className='calendar-body'>
          {_.map(this.shortDayNames, (day,index) =>
            <div className='grid-square-container' key={day+index}>
              <div
                key={index}
                unselectable='on'
                onMouseDown={()=>false}
                className='grid-square title'>
                {day}
              </div>
            </div>
          )}
          {_.flatMap(getDayArray(this.getMonthMoment(), this.props.selectedDate), (row,rowIndex) =>
            _.map(row, (dayObj,colIndex) =>
              <div className={this.getGridSquareBackgroundClasses(dayObj)}>
                <div unselectable='on'
                  onMouseDown={()=>false}
                  onClick={()=>this.props.onSelect(dayObj.isoString)}
                  key={rowIndex+''+colIndex}
                  className={this.getGridSquareClasses(dayObj)}>
                  {dayObj.number}
                </div>
              </div>
            )
          )}
        </div>
      </div>
    )
  }
}

export default AssignmentPopupCalendar
